import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { syncFromAnotherSource, getCollabState } from '@atlaskit/prosemirror-collab';
import { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { StepsDatabase } from '../stepsDatabase';
import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import { resetProsemirrorHistoryPlugin } from '../history';

export type Status =
	| 'granted_initial'
	| 'granted_recovery'
	| 'waiting_initial'
	| 'waiting_recovery'
	| 'init';

export const nextStatus = (status: Status, granted?: boolean) => {
	switch (status) {
		case 'init':
			return granted ? 'granted_initial' : 'waiting_initial';
		case 'waiting_initial':
		case 'waiting_recovery':
			return granted ? 'granted_recovery' : 'waiting_recovery';
		case 'granted_initial':
		case 'granted_recovery':
		default:
			return status;
	}
};

export const hasHistory = (api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined) => {
	const historyState = api?.history?.sharedState?.currentState?.();
	return (historyState?.done?.eventCount ?? 0) > 0 || (historyState?.undone?.eventCount ?? 0) > 0;
};

export async function resyncOnline(
	api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined,
	stepsDatabase: StepsDatabase,
	editorView: EditorView,
) {
	if (api?.connectivity?.sharedState.currentState()?.mode === 'offline') {
		return;
	}

	let status: Status = 'init';

	const lockCallback = async (granted?: boolean) => {
		status = nextStatus(status, granted);
		const { steps, docJSON, version } = await stepsDatabase.getData(editorView?.state?.schema);

		switch (status) {
			case 'granted_initial':
				const unconfirmedSteps = getCollabState(editorView.state)?.unconfirmed ?? [];
				unconfirmedSteps.forEach((s) => {
					if (s.origin instanceof Transaction) {
						s.origin.setMeta('isOffline', false);
					}
				});
				break;

			case 'waiting_initial':
				// Reset to base document so we don't duplicate the unconfirmed steps, when we come back online we'll sync with the latest changes
				if (steps.length > 0 && docJSON && version !== null) {
					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}
					editorView.dispatch(syncFromAnotherSource(editorView.state, version, docJSON, []));
					stepsDatabase.acquireSyncLock(lockCallback, true);
				}
				break;

			case 'granted_recovery':
				if (steps.length > 0 && docJSON && version !== null) {
					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}
					editorView.dispatch(syncFromAnotherSource(editorView.state, version, docJSON, steps));
				}
				break;

			case 'waiting_recovery':
				if (steps.length > 0 && docJSON && version !== null) {
					stepsDatabase.acquireSyncLock(lockCallback, true);
				}
				break;

			default:
				break;
		}
	};

	stepsDatabase.acquireSyncLock(lockCallback, false);
}
